import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { AlertTriangle, Plus, FileText, Download, Trash2, Sparkles, Eye, EyeOff, WifiOff, RefreshCw, Cloud } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import IncidentAIAnalysis from '@/components/IncidentAIAnalysis';
import IncidentPhotoUpload from '@/components/IncidentPhotoUpload';
import ViewAuditDialog from '@/components/ViewAuditDialog';
import { useOffline } from '@/contexts/OfflineContext';
import { getOfflineIncidents, queueIncidentCreate } from '@/services/syncService';
import { saveBulkToStore } from '@/services/offlineDB';
import { useTranslation } from 'react-i18next';
import PolicyViolations from '@/components/PolicyViolations';

import { rowId, withRowIds, rowKey, stripRowIds } from '@/lib/rowKeys';

const SupervisorIncidents = ({ user }) => {
  const { t } = useTranslation();
  const [incidentReports, setIncidentReports] = useState([]);
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [aiAssisting, setAiAssisting] = useState(false);
  const [aiRewriting, setAiRewriting] = useState(false);
  const [aiAnalyzing, setAiAnalyzing] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [editableAiAnalysis, setEditableAiAnalysis] = useState(null); // Editable copy of AI analysis
  const [pendingPhotos, setPendingPhotos] = useState([]); // Photos taken before report is saved
  const [divisions, setDivisions] = useState([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState(user?.divisionId || '');
  const [isOfflineData, setIsOfflineData] = useState(false);
  
  // Offline context
  const { isOnline, isOffline, updatePendingCount } = useOffline();
  
  // State for viewing linked documents
  const [viewingAudit, setViewingAudit] = useState(null);
  const [viewingJSA, setViewingJSA] = useState(null);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [showJSADialog, setShowJSADialog] = useState(false);
  
  const [incidentForm, setIncidentForm] = useState({
    incidentType: 'Injury',
    severity: 'Low',
    incidentDate: new Date().toISOString().split('T')[0],
    incidentTime: new Date().toTimeString().slice(0,5),
    location: '',
    description: '',
    peopleInvolved: [{ _rowId: rowId(), name: '', role: 'worker', details: '' }],
    witnesses: [''],
    witnessStatements: [],
    injuredParties: [],
    equipmentInvolved: '',
    propertyDamaged: '',
    correctiveActions: '',
    preventiveMeasures: '',
    supervisorName: '',
    reportedBy: '',
    status: 'draft'
  });

  useEffect(() => {
    loadIncidentReports();
    loadDivisions();
  }, [isOnline]);
  
  const loadDivisions = async () => {
    if (!isOnline) return;
    try {
      const response = await axiosInstance.get('/divisions');
      setDivisions(response.data);
      // Pre-select only the user's own division. If the user has no division,
      // leave it blank so they explicitly pick one (or submit without).
      if (user?.divisionId) {
        setSelectedDivisionId(user.divisionId);
      }
    } catch (error) {
      console.error('Failed to load divisions:', error);
    }
  };

  const loadIncidentReports = async () => {
    setLoading(true);
    
    // Try online first, fall back to offline
    if (isOnline) {
      try {
        const response = await axiosInstance.get('/incident-reports');
        setIncidentReports(response.data);
        setIsOfflineData(false);
        
        // Cache incidents for offline use
        if (response.data && response.data.length > 0) {
          await saveBulkToStore('incidents', response.data);
          console.log('📦 Cached', response.data.length, 'incidents for offline');
        }
      } catch (error) {
        console.error('Failed to load incidents online:', error);
        // Only fall back to offline cache if we still have zero reports loaded.
        // Never wipe the current list or show a "no cache" toast just because
        // ONE background refresh hiccuped after a successful save.
        if (!incidentReports || incidentReports.length === 0) {
          await loadOfflineIncidents({ silent: true });
        }
      }
    } else {
      await loadOfflineIncidents();
    }
    
    setLoading(false);
  };
  
  const loadOfflineIncidents = async ({ silent = false } = {}) => {
    try {
      const offlineIncidents = await getOfflineIncidents();
      if (offlineIncidents && offlineIncidents.length > 0) {
        setIncidentReports(offlineIncidents);
        setIsOfflineData(true);
        if (!silent) {
          toast.info('📴 Showing cached incidents (offline mode)', { duration: 3000 });
        }
      } else {
        setIncidentReports([]);
        if (!silent) {
          toast.warning(t('incidents.noCachedIncidents'), { duration: 4000 });
        }
      }
    } catch (error) {
      console.error('Failed to load offline incidents:', error);
      setIncidentReports([]);
    }
  };

  const handleCreateIncident = () => {
    setEditingReport(null);
    // Reset to user's own division (or blank if user has none) — avoids carry-over
    setSelectedDivisionId(user?.divisionId || '');
    setIncidentForm({
      incidentType: 'Injury',
      severity: 'Low',
      incidentDate: new Date().toISOString().split('T')[0],
      incidentTime: new Date().toTimeString().slice(0,5),
      location: '',
      description: '',
      peopleInvolved: [{ _rowId: rowId(), name: '', role: 'worker', details: '' }],
      witnesses: [''],
      witnessStatements: [],
      injuredParties: [],
      equipmentInvolved: '',
      propertyDamaged: '',
      correctiveActions: '',
      preventiveMeasures: '',
      supervisorName: user?.name || '',
      reportedBy: user?.name || '',
      status: 'draft'
    });
    setShowIncidentDialog(true);
    setPendingPhotos([]);
  };

  const handleEditIncident = (report) => {
    setEditingReport(report);
    setPendingPhotos([]);
    // Parse "Other: custom text" back into separate fields
    const knownTypes = ['Injury', 'Near Miss', 'Equipment Damage', 'Property Damage', 'Environmental', 'Other'];
    const isOtherType = report.incidentType?.startsWith('Other:') || !knownTypes.includes(report.incidentType);
    
    setIncidentForm({
      incidentType: isOtherType ? 'Other' : report.incidentType,
      otherIncidentType: isOtherType ? (report.incidentType?.replace('Other: ', '') || report.incidentType) : undefined,
      severity: report.severity,
      incidentDate: report.incidentDate,
      incidentTime: report.incidentTime,
      location: report.location,
      description: report.description,
      peopleInvolved: report.peopleInvolved?.length > 0 ? report.peopleInvolved : [{ _rowId: rowId(), name: '', role: 'worker', details: '' }],
      witnesses: report.witnesses?.length > 0 ? report.witnesses : [''],
      witnessStatements: report.witnessStatements || [],
      injuredParties: report.injuredParties || [],
      equipmentInvolved: report.equipmentInvolved || '',
      propertyDamaged: report.propertyDamaged || '',
      correctiveActions: report.correctiveActions || '',
      preventiveMeasures: report.preventiveMeasures || '',
      supervisorName: report.supervisorName || '',
      reportedBy: report.reportedBy || '',
      photos: report.photos || [],
      photoUrls: report.photoUrls || [],
      status: report.status || 'draft'
    });

    // Load division from the report (so user can edit it)
    setSelectedDivisionId(report.divisionId || '');
    
    // Restore AI analysis from stored report (so lessons learned, root causes, etc. are visible)
    if (report.aiAnalysis && typeof report.aiAnalysis === 'object') {
      setAiAnalysis({ analysis: report.aiAnalysis });
      setEditableAiAnalysis({
        rootCauses: withRowIds(report.aiAnalysis.rootCauses || []),
        severityValidation: { ...(report.aiAnalysis.severityValidation || {}) },
        recommendations: withRowIds(report.aiAnalysis.recommendations || []),
        trainingRecommendations: withRowIds(report.aiAnalysis.trainingRecommendations || []),
        lessonsLearned: withRowIds(report.aiAnalysis.lessonsLearned || []),
        preventionStrategies: withRowIds(report.aiAnalysis.preventionStrategies || [])
      });
    } else {
      setAiAnalysis(null);
      setEditableAiAnalysis(null);
    }
    
    setShowIncidentDialog(true);
  };

  const handleSubmitIncident = async (asDraft = false) => {
    if (!incidentForm.location || !incidentForm.description) {
      toast.error(t('incidents.fillRequired'));
      return;
    }

    const reportData = {
      ...incidentForm,
      // Resolve "Other" incident type — use custom text if provided
      incidentType: incidentForm.incidentType === 'Other' && incidentForm.otherIncidentType
        ? `Other: ${incidentForm.otherIncidentType}`
        : incidentForm.incidentType,
      status: asDraft ? 'draft' : 'submitted',
      reportedBy: incidentForm.reportedBy || user.name,
      reporterId: user.id,
      divisionId: selectedDivisionId || user?.divisionId || null,
      companyId: user.companyId,
      // Include edited AI analysis if available (save flat, not nested)
      ...(editableAiAnalysis && {
        aiAnalysis: stripRowIds(editableAiAnalysis)
      })
    };
    // Clean up temporary field
    delete reportData.otherIncidentType;

    // If offline and creating new report, queue for later sync
    if (isOffline && !editingReport) {
      try {
        setSubmitting(true);
        await queueIncidentCreate(reportData);
        await updatePendingCount();
        
        // Add to local list with pending flag
        const offlineIncident = {
          ...reportData,
          id: `offline-${Date.now()}`,
          createdAt: new Date().toISOString(),
          pendingSync: true
        };
        setIncidentReports(prev => [offlineIncident, ...prev]);
        
        toast.success('📴 Incident saved offline! Will sync when connected.', { duration: 4000 });
        setShowIncidentDialog(false);
        resetForm();
      } catch (error) {
        console.error('Failed to save incident offline:', error);
        toast.error(t('incidents.savedOfflineFailed'));
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Online submission
    try {
      setSubmitting(true);

      let savedReport = null;
      if (editingReport) {
        // Update existing report
        const resp = await axiosInstance.put(`/incident-reports/${editingReport.id}`, reportData);
        savedReport = resp.data || { ...editingReport, ...reportData };
        toast.success(t('incidents.updatedSuccess'));
      } else {
        // Create new report
        const resp = await axiosInstance.post('/incident-reports', reportData);
        savedReport = resp.data || { ...reportData, id: `local-${Date.now()}`, createdAt: new Date().toISOString() };
        toast.success(asDraft ? 'Draft saved' : 'Incident report submitted successfully');
      }

      // Optimistic update — immediately reflect the change in the list so the
      // user sees it even if a background refresh flakes out.
      if (savedReport) {
        setIncidentReports(prev => {
          const arr = Array.isArray(prev) ? prev : [];
          const idx = arr.findIndex(r => r.id === savedReport.id);
          if (idx >= 0) {
            const copy = [...arr];
            copy[idx] = { ...arr[idx], ...savedReport };
            return copy;
          }
          return [savedReport, ...arr];
        });
      }

      setShowIncidentDialog(false);
      resetForm();
      // Refresh in the background — errors are swallowed by loadIncidentReports
      // when the current list is non-empty, so a hiccup no longer confuses the UI.
      loadIncidentReports();
    } catch (error) {
      console.error('Failed to submit incident:', error);
      
      // Handle validation errors from backend
      let errorMessage = 'Failed to submit report';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(err => {
            const field = err.loc?.join('.') || 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };
  
  // Reset form helper
  const resetForm = () => {
    setIncidentForm({
      incidentType: 'Injury',
      severity: 'Low',
      incidentDate: new Date().toISOString().split('T')[0],
      incidentTime: new Date().toTimeString().slice(0,5),
      location: '',
      description: '',
      peopleInvolved: [{ _rowId: rowId(), name: '', role: 'worker', details: '' }],
      witnesses: [''],
      witnessStatements: [],
      injuredParties: [],
      immediateActions: '',
      rootCause: '',
      correctiveActions: '',
      photos: [],
      photoUrls: []
    });
    setAiAnalysis(null);
    setEditableAiAnalysis(null);
    setEditingReport(null);
  };

  const addPersonInvolved = () => {
    setIncidentForm({
      ...incidentForm,
      peopleInvolved: [...incidentForm.peopleInvolved, { _rowId: rowId(), name: '', role: 'worker', details: '' }]
    });
  };

  const removePersonInvolved = (index) => {
    const updated = incidentForm.peopleInvolved.filter((_, i) => i !== index);
    setIncidentForm({ ...incidentForm, peopleInvolved: updated });
  };

  const updatePersonInvolved = (index, field, value) => {
    const updated = [...incidentForm.peopleInvolved];
    updated[index] = { ...updated[index], [field]: value };
    setIncidentForm({ ...incidentForm, peopleInvolved: updated });
  };

  const addWitness = () => {
    setIncidentForm({
      ...incidentForm,
      witnesses: [...incidentForm.witnesses, '']
    });
  };

  const removeWitness = (index) => {
    const updated = incidentForm.witnesses.filter((_, i) => i !== index);
    setIncidentForm({ ...incidentForm, witnesses: updated });
  };

  const updateWitness = (index, value) => {
    const updated = [...incidentForm.witnesses];
    updated[index] = value;
    setIncidentForm({ ...incidentForm, witnesses: updated });
  };

  // Witness Statement handlers
  const addWitnessStatement = () => {
    setIncidentForm({
      ...incidentForm,
      witnessStatements: [...incidentForm.witnessStatements, { _rowId: rowId(), witnessName: '', statement: '', timestamp: new Date().toISOString() }]
    });
  };

  const removeWitnessStatement = (index) => {
    const updated = incidentForm.witnessStatements.filter((_, i) => i !== index);
    setIncidentForm({ ...incidentForm, witnessStatements: updated });
  };

  const updateWitnessStatement = (index, field, value) => {
    const updated = [...incidentForm.witnessStatements];
    updated[index] = { ...updated[index], [field]: value };
    setIncidentForm({ ...incidentForm, witnessStatements: updated });
  };

  // Injured Parties Handlers
  const BODY_PARTS = [
    // Head & Face - Detailed
    { value: 'head', label: 'Head (General)', view: 'front', x: '29%', y: '6%' },
    { value: 'skull', label: 'Skull/Top of Head', view: 'front', x: '29%', y: '3%' },
    { value: 'forehead', label: 'Forehead', view: 'front', x: '29%', y: '7%' },
    { value: 'leftEye', label: 'Left Eye', view: 'front', x: '27%', y: '9%' },
    { value: 'rightEye', label: 'Right Eye', view: 'front', x: '31%', y: '9%' },
    { value: 'leftEar', label: 'Left Ear', view: 'front', x: '24%', y: '10%' },
    { value: 'rightEar', label: 'Right Ear', view: 'front', x: '34%', y: '10%' },
    { value: 'nose', label: 'Nose', view: 'front', x: '29%', y: '11%' },
    { value: 'mouth', label: 'Mouth/Lips', view: 'front', x: '29%', y: '13%' },
    { value: 'jaw', label: 'Jaw/Chin', view: 'front', x: '29%', y: '15%' },
    { value: 'teeth', label: 'Teeth', view: 'front', x: '29%', y: '14%' },
    { value: 'face', label: 'Face (General)', view: 'front', x: '29%', y: '10%' },
    // Neck
    { value: 'neck', label: 'Neck (Front)', view: 'front', x: '29%', y: '18%' },
    { value: 'neckBack', label: 'Neck (Back)', view: 'back', x: '71%', y: '18%' },
    { value: 'throat', label: 'Throat', view: 'front', x: '29%', y: '19%' },
    // Shoulders & Arms
    { value: 'leftShoulder', label: 'Left Shoulder', view: 'front', x: '24%', y: '24%' },
    { value: 'rightShoulder', label: 'Right Shoulder', view: 'front', x: '34%', y: '24%' },
    { value: 'leftArm', label: 'Left Arm', view: 'front', x: '20%', y: '35%' },
    { value: 'rightArm', label: 'Right Arm', view: 'front', x: '38%', y: '35%' },
    { value: 'leftElbow', label: 'Left Elbow', view: 'front', x: '19%', y: '38%' },
    { value: 'rightElbow', label: 'Right Elbow', view: 'front', x: '39%', y: '38%' },
    { value: 'leftWrist', label: 'Left Wrist', view: 'front', x: '18%', y: '45%' },
    { value: 'rightWrist', label: 'Right Wrist', view: 'front', x: '40%', y: '45%' },
    { value: 'leftHand', label: 'Left Hand/Fingers', view: 'front', x: '18%', y: '48%' },
    { value: 'rightHand', label: 'Right Hand/Fingers', view: 'front', x: '40%', y: '48%' },
    // Torso
    { value: 'chest', label: 'Chest', view: 'front', x: '29%', y: '32%' },
    { value: 'ribs', label: 'Ribs', view: 'front', x: '29%', y: '36%' },
    { value: 'abdomen', label: 'Abdomen', view: 'front', x: '29%', y: '42%' },
    { value: 'upperBack', label: 'Upper Back', view: 'back', x: '71%', y: '32%' },
    { value: 'lowerBack', label: 'Lower Back', view: 'back', x: '71%', y: '42%' },
    { value: 'spine', label: 'Spine', view: 'back', x: '71%', y: '37%' },
    // Lower Body
    { value: 'hip', label: 'Hip', view: 'front', x: '29%', y: '52%' },
    { value: 'groin', label: 'Groin', view: 'front', x: '29%', y: '54%' },
    { value: 'leftThigh', label: 'Left Thigh', view: 'front', x: '26%', y: '58%' },
    { value: 'rightThigh', label: 'Right Thigh', view: 'front', x: '32%', y: '58%' },
    { value: 'leftLeg', label: 'Left Leg', view: 'front', x: '26%', y: '62%' },
    { value: 'rightLeg', label: 'Right Leg', view: 'front', x: '32%', y: '62%' },
    { value: 'leftKnee', label: 'Left Knee', view: 'front', x: '26%', y: '68%' },
    { value: 'rightKnee', label: 'Right Knee', view: 'front', x: '32%', y: '68%' },
    { value: 'leftShin', label: 'Left Shin', view: 'front', x: '26%', y: '75%' },
    { value: 'rightShin', label: 'Right Shin', view: 'front', x: '32%', y: '75%' },
    { value: 'leftCalf', label: 'Left Calf', view: 'back', x: '68%', y: '75%' },
    { value: 'rightCalf', label: 'Right Calf', view: 'back', x: '74%', y: '75%' },
    { value: 'leftAnkle', label: 'Left Ankle', view: 'front', x: '26%', y: '82%' },
    { value: 'rightAnkle', label: 'Right Ankle', view: 'front', x: '32%', y: '82%' },
    { value: 'leftFoot', label: 'Left Foot', view: 'front', x: '26%', y: '88%' },
    { value: 'rightFoot', label: 'Right Foot', view: 'front', x: '32%', y: '88%' },
    { value: 'leftToes', label: 'Left Toes', view: 'front', x: '26%', y: '91%' },
    { value: 'rightToes', label: 'Right Toes', view: 'front', x: '32%', y: '91%' },
    // Internal/Other
    { value: 'internal', label: 'Internal Organs', view: 'front', x: '29%', y: '38%' },
    { value: 'multiple', label: 'Multiple Body Parts', view: 'front', x: '29%', y: '50%' },
  ];

  const addInjuredParty = () => {
    setIncidentForm({
      ...incidentForm,
      injuredParties: [...incidentForm.injuredParties, { 
        _rowId: rowId(),
        name: '', 
        bodyPart: '', 
        injuryType: '', 
        injuryDescription: '',
        treatmentRequired: ''
      }]
    });
  };

  const removeInjuredParty = (index) => {
    const updated = incidentForm.injuredParties.filter((_, i) => i !== index);
    setIncidentForm({ ...incidentForm, injuredParties: updated });
  };

  const updateInjuredParty = (index, field, value) => {
    const updated = [...incidentForm.injuredParties];
    updated[index] = { ...updated[index], [field]: value };
    
    // Auto-fill position data when body part is selected
    if (field === 'bodyPart') {
      const bodyPartInfo = BODY_PARTS.find(bp => bp.value === value);
      if (bodyPartInfo) {
        updated[index] = {
          ...updated[index],
          view: bodyPartInfo.view,
          x: bodyPartInfo.x,
          y: bodyPartInfo.y
        };
      }
    }
    
    setIncidentForm({ ...incidentForm, injuredParties: updated });
  };

  const handleAIAssist = async () => {
    if (!incidentForm.description || !incidentForm.location) {
      toast.error(t('incidents.provideDescLocFirst'));
      return;
    }

    setAiAssisting(true);
    try {
      const response = await axiosInstance.post('/incident-reports/ai-assist', {
        description: incidentForm.description,
        location: incidentForm.location,
        incidentType: incidentForm.incidentType,
        severity: incidentForm.severity
      });

      // AI suggestions for form fields
      const suggestions = response.data;
      
      if (suggestions.correctiveActions) {
        setIncidentForm(prev => ({
          ...prev,
          correctiveActions: prev.correctiveActions || suggestions.correctiveActions
        }));
      }
      
      if (suggestions.preventiveMeasures) {
        setIncidentForm(prev => ({
          ...prev,
          preventiveMeasures: prev.preventiveMeasures || suggestions.preventiveMeasures
        }));
      }

      toast.success('AI suggestions added to form!');
    } catch (error) {
      console.error('AI assistance failed:', error);
      toast.error('Failed to get AI assistance: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAiAssisting(false);
    }
  };

  const handleRewriteDescription = async () => {
    if (!incidentForm.description) {
      toast.error('Please enter a description first');
      return;
    }

    setAiRewriting(true);
    try {
      const response = await axiosInstance.post('/incident-reports/rewrite-description', {
        description: incidentForm.description,
        incidentType: incidentForm.incidentType,
        location: incidentForm.location,
        severity: incidentForm.severity
      });

      // Replace description with AI-rewritten version
      setIncidentForm(prev => ({
        ...prev,
        description: response.data.rewrittenDescription
      }));

      toast.success('Description rewritten professionally!');
    } catch (error) {
      console.error('AI rewrite failed:', error);
      toast.error('Failed to rewrite description: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAiRewriting(false);
    }
  };

  // Handlers for viewing linked documents
  const handleViewAudit = async (auditId) => {
    try {
      const response = await axiosInstance.get(`/safety-audits/${auditId}`);
      setViewingAudit(response.data);
      setShowAuditDialog(true);
    } catch (error) {
      console.error('Failed to load audit:', error);
      toast.error('Failed to load audit details');
    }
  };

  const handleViewJSA = async (jsaId) => {
    try {
      const response = await axiosInstance.get(`/jsa/${jsaId}`);
      setViewingJSA(response.data);
      setShowJSADialog(true);
    } catch (error) {
      console.error('Failed to load JSA:', error);
      toast.error('Failed to load JSA details');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => window.history.back()}
          className="mb-4 hover:bg-gray-100"
          data-testid="back-button"
        >
          ← {t('common.back')}
        </Button>

        {/* Offline Mode Banner */}
        {!isOnline && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-3">
            <WifiOff className="h-5 w-5 text-orange-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800">{t('offline.offlineMode')}</p>
              <p className="text-xs text-orange-600">{t('offline.viewingCachedData')}</p>
            </div>
          </div>
        )}

        <Card>
          <CardHeader className="pb-3 sm:pb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <CardTitle className="flex items-center text-lg sm:text-xl">
                  <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-red-600" />
                  {t('incidents.title')}
                </CardTitle>
                <CardDescription className="text-xs sm:text-sm">{t('incidents.subtitle')}</CardDescription>
              </div>
              <Button onClick={handleCreateIncident} className="bg-red-600 hover:bg-red-700 w-full sm:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                {t('home.newReport')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
              </div>
            ) : incidentReports.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <AlertTriangle className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-gray-300" />
                <p>{t('incidents.noIncidentReports')}</p>
                <p className="text-xs sm:text-sm">{t('incidents.clickToViewDetails')}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {incidentReports.map((report) => (
                  <div key={report.id} className="border rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow">
                    {/* Mobile-first layout */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        {/* Title and badges - stack on mobile */}
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2">
                          <h3 className="font-semibold text-base sm:text-lg">{report.incidentType}</h3>
                          <Badge className={`text-xs ${
                            report.severity === 'Critical' ? 'bg-red-600' :
                            report.severity === 'High' ? 'bg-orange-600' :
                            report.severity === 'Medium' ? 'bg-yellow-600' :
                            report.severity === 'Non-Medical' ? 'bg-gray-500' : 'bg-blue-600'
                          }`}>
                            {report.severity}
                          </Badge>
                          <Badge variant={report.status === 'submitted' ? 'default' : 'secondary'} className="text-xs">
                            {report.status}
                          </Badge>
                        </div>
                        
                        {/* Location and date */}
                        <p className="text-xs sm:text-sm text-gray-600 mb-1 truncate">
                          {report.location} • {report.incidentDate} {report.incidentTime}
                        </p>
                        {/* Supervisor and Reported By */}
                        <p className="text-xs text-gray-500 mb-2">
                          {report.supervisorName && <span>{t('incidents.supervisor')}: {report.supervisorName}</span>}
                          {report.supervisorName && report.reportedBy && <span> • </span>}
                          {report.reportedBy && <span>{t('incidents.reported')}: {report.reportedBy}</span>}
                        </p>
                        
                        {/* Description - show less on mobile */}
                        <p className="text-xs sm:text-sm text-gray-700 line-clamp-2 mb-2">{report.description}</p>
                        
                        {/* Related JSAs and Audits - compact on mobile */}
                        {(report.linkedJSAs?.length > 0 || report.linkedAudits?.length > 0) && (
                          <div className="mt-2 pt-2 border-t space-y-1.5">
                            {report.linkedJSAs?.length > 0 && (
                              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs py-0">
                                  🔗 {report.linkedJSAs.length} Linked JSA{report.linkedJSAs.length > 1 ? 's' : ''}
                                </Badge>
                                <span className="text-gray-600 truncate max-w-[150px] sm:max-w-none">
                                  {report.linkedJSAs.slice(0, 2).map(jsa => jsa.task).join(', ')}
                                  {report.linkedJSAs.length > 2 && ` +${report.linkedJSAs.length - 2} more`}
                                </span>
                              </div>
                            )}
                            {report.linkedAudits?.length > 0 && (
                              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs py-0">
                                  🔗 {report.linkedAudits.length} Linked Audit{report.linkedAudits.length > 1 ? 's' : ''}
                                </Badge>
                                <span className="text-gray-600 truncate">
                                  Last audit: {report.linkedAudits[0]?.date?.split('T')[0]}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Action buttons - horizontal scroll on mobile, row on desktop */}
                      <div className="flex gap-2 sm:ml-4 overflow-x-auto pb-1 sm:pb-0 sm:flex-shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditIncident(report)}
                          className="flex-shrink-0 h-8 px-2 sm:px-3"
                        >
                          <FileText className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">{t('common.view')}/{t('common.edit')}</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-blue-600 hover:text-blue-700 hover:border-blue-600 flex-shrink-0 h-8 px-2 sm:px-3"
                          onClick={async () => {
                            try {
                              const response = await axiosInstance.get(`/incident-reports/${report.id}/download-pdf`, {
                                responseType: 'blob'
                              });
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `incident_report_${report.id}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                              toast.success('PDF downloaded');
                            } catch (error) {
                              console.error('Failed to download PDF:', error);
                              toast.error('Failed to download PDF');
                            }
                          }}
                        >
                          <Download className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">{t('common.download')}</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-gray-600 hover:text-gray-800 hover:border-gray-500 flex-shrink-0 h-8 px-2 sm:px-3"
                          data-testid={`remove-incident-from-page-${report.id}`}
                          onClick={async () => {
                            if (!window.confirm(`Remove this ${report.incidentType} report from your page?\n\nIt stays on file for your administrator, along with its full history. This only clears it from your own list.`)) {
                              return;
                            }
                            try {
                              await axiosInstance.delete(`/incident-reports/${report.id}`);
                              toast.success('Removed from your page. Your administrator still has the record.');
                              loadIncidentReports();
                            } catch (error) {
                              console.error('Failed to remove report:', error);
                              toast.error(error.response?.data?.detail || 'Failed to remove report');
                            }
                          }}
                        >
                          <EyeOff className="w-4 h-4 sm:mr-1" />
                          <span className="hidden sm:inline">Remove from my page</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Incident Report Dialog */}
      <Dialog open={showIncidentDialog} onOpenChange={setShowIncidentDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingReport ? t('incidents.editIncident') : t('incidents.newIncident')}</DialogTitle>
            <DialogDescription>{t('incidents.subtitle')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('incidents.incidentType')} *</label>
                <select
                  value={incidentForm.incidentType === 'Other' || !['Injury', 'Near Miss', 'Equipment Damage', 'Property Damage', 'Environmental'].includes(incidentForm.incidentType) ? 'Other' : incidentForm.incidentType}
                  onChange={(e) => {
                    if (e.target.value === 'Other') {
                      setIncidentForm({...incidentForm, incidentType: 'Other', otherIncidentType: ''});
                    } else {
                      setIncidentForm({...incidentForm, incidentType: e.target.value, otherIncidentType: undefined});
                    }
                  }}
                  className="w-full border rounded px-3 py-2 mt-1"
                >
                  <option value="Injury">{t('incidents.injury')}</option>
                  <option value="Near Miss">{t('incidents.nearMiss')}</option>
                  <option value="Equipment Damage">{t('incidents.equipmentInvolved')}</option>
                  <option value="Property Damage">{t('incidents.propertyDamage')}</option>
                  <option value="Environmental">{t('incidents.environmental')}</option>
                  <option value="Other">{t('incidents.other')}</option>
                </select>
                {(incidentForm.incidentType === 'Other' || incidentForm.otherIncidentType !== undefined) && (
                  <input
                    type="text"
                    placeholder={t('incidents.otherTypePlaceholder')}
                    value={incidentForm.otherIncidentType || ''}
                    onChange={(e) => setIncidentForm({...incidentForm, otherIncidentType: e.target.value})}
                    className="w-full border rounded px-3 py-2 mt-2"
                    data-testid="other-incident-type-input"
                  />
                )}
              </div>
              <div>
                <label className="text-sm font-medium">{t('incidents.severity')} *</label>
                <select
                  value={incidentForm.severity}
                  onChange={(e) => setIncidentForm({...incidentForm, severity: e.target.value})}
                  className="w-full border rounded px-3 py-2 mt-1"
                >
                  <option value="Non-Medical">{t('incidents.nonMedical')}</option>
                  <option value="Low">{t('incidents.lowSeverity')}</option>
                  <option value="Medium">{t('incidents.mediumSeverity')}</option>
                  <option value="High">{t('incidents.highSeverity')}</option>
                  <option value="Critical">{t('incidents.criticalSeverity')}</option>
                </select>
                {/* Severity Definition Help */}
                <div className="mt-2 p-3 bg-gray-50 rounded-lg border text-xs space-y-1">
                  <div className="flex items-start gap-2">
                    <span className="w-2 h-2 mt-1 rounded-full bg-gray-400 flex-shrink-0"></span>
                    <span><strong>{t('incidents.nonMedical')}:</strong> {t('incidents.nonMedicalDesc')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-2 h-2 mt-1 rounded-full bg-blue-500 flex-shrink-0"></span>
                    <span><strong>{t('incidents.lowSeverity').split(' - ')[0]}:</strong> {t('incidents.lowSeverityDesc')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-2 h-2 mt-1 rounded-full bg-yellow-500 flex-shrink-0"></span>
                    <span><strong>{t('incidents.mediumSeverity').split(' - ')[0]}:</strong> {t('incidents.mediumSeverityDesc')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-2 h-2 mt-1 rounded-full bg-orange-500 flex-shrink-0"></span>
                    <span><strong>{t('incidents.highSeverity').split(' - ')[0]}:</strong> {t('incidents.highSeverityDesc')}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="w-2 h-2 mt-1 rounded-full bg-red-500 flex-shrink-0"></span>
                    <span><strong>{t('incidents.criticalSeverity').split(' - ')[0]}:</strong> {t('incidents.criticalSeverityDesc')}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('common.date')} *</label>
                <input
                  type="date"
                  value={incidentForm.incidentDate}
                  onChange={(e) => setIncidentForm({...incidentForm, incidentDate: e.target.value})}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('common.time')} *</label>
                <input
                  type="time"
                  value={incidentForm.incidentTime}
                  onChange={(e) => setIncidentForm({...incidentForm, incidentTime: e.target.value})}
                  className="w-full border rounded px-3 py-2 mt-1"
                />
              </div>
            </div>

            {/* Supervisor Name and Reported By */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">{t('incidents.supervisorName')} *</label>
                <input
                  type="text"
                  value={incidentForm.supervisorName}
                  onChange={(e) => setIncidentForm({...incidentForm, supervisorName: e.target.value})}
                  placeholder={t('incidents.supervisorNamePlaceholder')}
                  className="w-full border rounded px-3 py-2 mt-1"
                  data-testid="supervisor-name-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium">{t('incidents.reportedBy')} *</label>
                <input
                  type="text"
                  value={incidentForm.reportedBy}
                  onChange={(e) => setIncidentForm({...incidentForm, reportedBy: e.target.value})}
                  placeholder={t('incidents.reportedByPlaceholder')}
                  className="w-full border rounded px-3 py-2 mt-1"
                  data-testid="reported-by-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">{t('common.location')} *</label>
              <input
                type="text"
                value={incidentForm.location}
                onChange={(e) => setIncidentForm({...incidentForm, location: e.target.value})}
                placeholder={t('incidents.locationPlaceholder')}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium">{t('incidents.divisionLabel')}</label>
              <select
                value={selectedDivisionId || ''}
                onChange={(e) => setSelectedDivisionId(e.target.value)}
                className="w-full border rounded px-3 py-2 mt-1 bg-white"
                data-testid="incident-division-select"
              >
                <option value="">{t('incidents.noDivisionOption')}</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">{t('incidents.divisionHelper')}</p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium">{t('common.description')} *</label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleRewriteDescription}
                  disabled={aiRewriting || !incidentForm.description}
                  className="text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
                >
                  <Sparkles className="w-3 h-3 mr-1" />
                  {aiRewriting ? t('common.loading') : t('incidents.rewriteWithAi')}
                </Button>
              </div>
              <textarea
                value={incidentForm.description}
                onChange={(e) => setIncidentForm({...incidentForm, description: e.target.value})}
                placeholder={t('incidents.descriptionPlaceholder')}
                rows={4}
                className="w-full border rounded px-3 py-2"
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Write in your own words, then click AI Rewrite to convert to technical language
              </p>
            </div>

            {/* People Involved */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">{t('incidents.peopleInvolved')}</label>
                <Button size="sm" type="button" onClick={addPersonInvolved} variant="outline">
                  <Plus className="w-4 h-4 mr-1" />
                  {t('incidents.addPerson')}
                </Button>
              </div>
              {incidentForm.peopleInvolved.map((person, index) => (
                <div key={person._rowId || `person-${index}`} className="border rounded p-3 mb-2">
                  <div className="grid grid-cols-3 gap-2 mb-2">
                    <input
                      type="text"
                      value={person.name}
                      onChange={(e) => updatePersonInvolved(index, 'name', e.target.value)}
                      placeholder={t('common.name')}
                      className="border rounded px-2 py-1"
                    />
                    <select
                      value={person.role}
                      onChange={(e) => updatePersonInvolved(index, 'role', e.target.value)}
                      className="border rounded px-2 py-1"
                    >
                      <option value="worker">{t('incidents.roleWorker')}</option>
                      <option value="supervisor">{t('incidents.roleSupervisor')}</option>
                      <option value="contractor">{t('incidents.roleContractor')}</option>
                      <option value="visitor">{t('incidents.roleVisitor')}</option>
                    </select>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => removePersonInvolved(index)}
                      disabled={incidentForm.peopleInvolved.length === 1}
                    >
                      {t('common.remove')}
                    </Button>
                  </div>
                  <input
                    type="text"
                    value={person.details}
                    onChange={(e) => updatePersonInvolved(index, 'details', e.target.value)}
                    placeholder={t('incidents.additionalDetails')}
                    className="w-full border rounded px-2 py-1"
                  />
                </div>
              ))}
            </div>

            {/* Witnesses */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">{t('incidents.witnesses')}</label>
                <Button size="sm" type="button" onClick={addWitness} variant="outline">
                  <Plus className="w-4 h-4 mr-1" />
                  {t('incidents.addWitness')}
                </Button>
              </div>
              {incidentForm.witnesses.map((witness, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={witness}
                    onChange={(e) => updateWitness(index, e.target.value)}
                    placeholder={t('incidents.witnessNamePlaceholder')}
                    className="flex-1 border rounded px-2 py-1"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeWitness(index)}
                    disabled={incidentForm.witnesses.length === 1}
                  >
                    {t('common.remove')}
                  </Button>
                </div>
              ))}
            </div>

            {/* Witness Statements */}
            <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {t('incidents.witnessStatements')}
                </label>
                <Button size="sm" type="button" onClick={addWitnessStatement} variant="outline" className="border-blue-300">
                  <Plus className="w-4 h-4 mr-1" />
                  {t('incidents.addStatement')}
                </Button>
              </div>
              {incidentForm.witnessStatements.length === 0 ? (
                <p className="text-sm text-gray-600 italic">{t('incidents.noWitnessStatements')}</p>
              ) : (
                <div className="space-y-3">
                  {incidentForm.witnessStatements.map((statement, index) => (
                    <div key={statement._rowId || `stmt-${index}`} className="bg-white p-3 rounded border border-blue-100">
                      <div className="flex justify-between items-start mb-2">
                        <input
                          type="text"
                          value={statement.witnessName}
                          onChange={(e) => updateWitnessStatement(index, 'witnessName', e.target.value)}
                          placeholder={t('incidents.witnessNamePlaceholder')}
                          className="flex-1 border rounded px-2 py-1 font-medium"
                        />
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeWitnessStatement(index)}
                          className="ml-2"
                        >
                          {t('common.remove')}
                        </Button>
                      </div>
                      <textarea
                        value={statement.statement}
                        onChange={(e) => updateWitnessStatement(index, 'statement', e.target.value)}
                        placeholder={t('incidents.witnessStatementPlaceholder')}
                        className="w-full border rounded px-2 py-2 min-h-[100px]"
                        rows={4}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {t('incidents.recorded')}: {new Date(statement.timestamp).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Injured Parties - Body Part Selection for Body Map */}
            <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <label className="text-sm font-semibold text-red-900 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  {t('incidents.injuredPartiesTitle')}
                </label>
                <Button size="sm" type="button" onClick={addInjuredParty} variant="outline" className="border-red-300">
                  <Plus className="w-4 h-4 mr-1" />
                  {t('incidents.addInjuredPerson')}
                </Button>
              </div>
              {incidentForm.injuredParties.length === 0 ? (
                <p className="text-sm text-gray-600 italic">{t('incidents.noInjuredParties')}</p>
              ) : (
                <div className="space-y-3">
                  {incidentForm.injuredParties.map((party, index) => (
                    <div key={party._rowId || `injured-${index}`} className="bg-white p-3 rounded border border-red-100">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-red-900">{t('incidents.injuredPersonNum')}{index + 1}</span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => removeInjuredParty(index)}
                          className="text-red-600 border-red-300"
                        >
                          {t('common.remove')}
                        </Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-2">
                        <div>
                          <label className="text-xs text-gray-600">{t('common.name')}</label>
                          <input
                            type="text"
                            value={party.name}
                            onChange={(e) => updateInjuredParty(index, 'name', e.target.value)}
                            placeholder={t('incidents.injuredPersonsName')}
                            className="w-full border rounded px-2 py-1"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">{t('incidents.bodyPartInjured')} *</label>
                          <select
                            value={party.bodyPart}
                            onChange={(e) => updateInjuredParty(index, 'bodyPart', e.target.value)}
                            className="w-full border rounded px-2 py-1"
                          >
                            <option value="">{t('incidents.selectBodyPart')}</option>
                            <optgroup label={t('incidents.bodyGroupHeadGeneral')}>
                              <option value="head">{t('incidents.bodyHead')}</option>
                              <option value="skull">{t('incidents.bodySkull')}</option>
                              <option value="forehead">{t('incidents.bodyForehead')}</option>
                              <option value="face">{t('incidents.bodyFace')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupHeadEyes')}>
                              <option value="leftEye">{t('incidents.bodyLeftEye')}</option>
                              <option value="rightEye">{t('incidents.bodyRightEye')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupHeadEars')}>
                              <option value="leftEar">{t('incidents.bodyLeftEar')}</option>
                              <option value="rightEar">{t('incidents.bodyRightEar')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupHeadNoseMouth')}>
                              <option value="nose">{t('incidents.bodyNose')}</option>
                              <option value="mouth">{t('incidents.bodyMouth')}</option>
                              <option value="teeth">{t('incidents.bodyTeeth')}</option>
                              <option value="jaw">{t('incidents.bodyJaw')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupNeck')}>
                              <option value="neck">{t('incidents.bodyNeckFront')}</option>
                              <option value="neckBack">{t('incidents.bodyNeckBack')}</option>
                              <option value="throat">{t('incidents.bodyThroat')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupShoulders')}>
                              <option value="leftShoulder">{t('incidents.bodyLeftShoulder')}</option>
                              <option value="rightShoulder">{t('incidents.bodyRightShoulder')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupArms')}>
                              <option value="leftArm">{t('incidents.bodyLeftArm')}</option>
                              <option value="rightArm">{t('incidents.bodyRightArm')}</option>
                              <option value="leftElbow">{t('incidents.bodyLeftElbow')}</option>
                              <option value="rightElbow">{t('incidents.bodyRightElbow')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupWristsHands')}>
                              <option value="leftWrist">{t('incidents.bodyLeftWrist')}</option>
                              <option value="rightWrist">{t('incidents.bodyRightWrist')}</option>
                              <option value="leftHand">{t('incidents.bodyLeftHand')}</option>
                              <option value="rightHand">{t('incidents.bodyRightHand')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupTorsoFront')}>
                              <option value="chest">{t('incidents.bodyChest')}</option>
                              <option value="ribs">{t('incidents.bodyRibs')}</option>
                              <option value="abdomen">{t('incidents.bodyAbdomen')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupTorsoBack')}>
                              <option value="upperBack">{t('incidents.bodyUpperBack')}</option>
                              <option value="lowerBack">{t('incidents.bodyLowerBack')}</option>
                              <option value="spine">{t('incidents.bodySpine')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupHipGroin')}>
                              <option value="hip">{t('incidents.bodyHip')}</option>
                              <option value="groin">{t('incidents.bodyGroin')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupUpperLegs')}>
                              <option value="leftThigh">{t('incidents.bodyLeftThigh')}</option>
                              <option value="rightThigh">{t('incidents.bodyRightThigh')}</option>
                              <option value="leftLeg">{t('incidents.bodyLeftLeg')}</option>
                              <option value="rightLeg">{t('incidents.bodyRightLeg')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupKnees')}>
                              <option value="leftKnee">{t('incidents.bodyLeftKnee')}</option>
                              <option value="rightKnee">{t('incidents.bodyRightKnee')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupLowerLegs')}>
                              <option value="leftShin">{t('incidents.bodyLeftShin')}</option>
                              <option value="rightShin">{t('incidents.bodyRightShin')}</option>
                              <option value="leftCalf">{t('incidents.bodyLeftCalf')}</option>
                              <option value="rightCalf">{t('incidents.bodyRightCalf')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupAnklesFeet')}>
                              <option value="leftAnkle">{t('incidents.bodyLeftAnkle')}</option>
                              <option value="rightAnkle">{t('incidents.bodyRightAnkle')}</option>
                              <option value="leftFoot">{t('incidents.bodyLeftFoot')}</option>
                              <option value="rightFoot">{t('incidents.bodyRightFoot')}</option>
                              <option value="leftToes">{t('incidents.bodyLeftToes')}</option>
                              <option value="rightToes">{t('incidents.bodyRightToes')}</option>
                            </optgroup>
                            <optgroup label={t('incidents.bodyGroupOther')}>
                              <option value="internal">{t('incidents.bodyInternal')}</option>
                              <option value="multiple">{t('incidents.bodyMultiple')}</option>
                            </optgroup>
                          </select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-2">
                        <div>
                          <label className="text-xs text-gray-600">{t('incidents.injuryType')}</label>
                          <select
                            value={party.injuryType}
                            onChange={(e) => updateInjuredParty(index, 'injuryType', e.target.value)}
                            className="w-full border rounded px-2 py-1"
                          >
                            <option value="">{t('incidents.selectInjuryType')}</option>
                            <option value="cut">{t('incidents.injuryTypeCut')}</option>
                            <option value="bruise">{t('incidents.injuryTypeBruise')}</option>
                            <option value="strain">{t('incidents.injuryTypeStrain')}</option>
                            <option value="fracture">{t('incidents.injuryTypeFracture')}</option>
                            <option value="burn">{t('incidents.injuryTypeBurn')}</option>
                            <option value="puncture">{t('incidents.injuryTypePuncture')}</option>
                            <option value="crush">{t('incidents.injuryTypeCrush')}</option>
                            <option value="amputation">{t('incidents.injuryTypeAmputation')}</option>
                            <option value="other">{t('incidents.injuryTypeOther')}</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs text-gray-600">{t('incidents.treatmentRequired')}</label>
                          <select
                            value={party.treatmentRequired}
                            onChange={(e) => updateInjuredParty(index, 'treatmentRequired', e.target.value)}
                            className="w-full border rounded px-2 py-1"
                          >
                            <option value="">{t('incidents.selectTreatment')}</option>
                            <option value="firstAid">{t('incidents.treatmentFirstAid')}</option>
                            <option value="medical">{t('incidents.treatmentMedical')}</option>
                            <option value="hospital">{t('incidents.treatmentHospital')}</option>
                            <option value="surgery">{t('incidents.treatmentSurgery')}</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-gray-600">{t('incidents.injuryDescription')}</label>
                        <textarea
                          value={party.injuryDescription}
                          onChange={(e) => updateInjuredParty(index, 'injuryDescription', e.target.value)}
                          placeholder={t('incidents.injuryDescPlaceholder')}
                          className="w-full border rounded px-2 py-1"
                          rows={2}
                        />
                      </div>

                      {/* OSHA Form 301 required fields — always visible on any injured party */}
                      <div className="mt-3 border-t pt-3">
                        <div className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1">
                          OSHA Form 301 details
                          <span className="text-gray-500 font-normal">(required if recordable)</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                          <div>
                            <label className="text-gray-600">Job title</label>
                            <input
                              type="text"
                              value={party.jobTitle || ''}
                              onChange={(e) => updateInjuredParty(index, 'jobTitle', e.target.value)}
                              className="w-full border rounded px-2 py-1"
                              data-testid={`injured-jobtitle-${index}`}
                            />
                          </div>
                          <div>
                            <label className="text-gray-600">Date of birth</label>
                            <input
                              type="date"
                              value={party.dateOfBirth || ''}
                              onChange={(e) => updateInjuredParty(index, 'dateOfBirth', e.target.value)}
                              className="w-full border rounded px-2 py-1"
                              data-testid={`injured-dob-${index}`}
                            />
                          </div>
                          <div>
                            <label className="text-gray-600">Date hired</label>
                            <input
                              type="date"
                              value={party.dateHired || ''}
                              onChange={(e) => updateInjuredParty(index, 'dateHired', e.target.value)}
                              className="w-full border rounded px-2 py-1"
                              data-testid={`injured-datehired-${index}`}
                            />
                          </div>
                          <div>
                            <label className="text-gray-600">Gender</label>
                            <select
                              value={party.gender || ''}
                              onChange={(e) => updateInjuredParty(index, 'gender', e.target.value)}
                              className="w-full border rounded px-2 py-1"
                              data-testid={`injured-gender-${index}`}
                            >
                              <option value="">—</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="nonbinary">Non-binary</option>
                              <option value="undisclosed">Prefer not to say</option>
                            </select>
                          </div>
                          <div>
                            <label className="text-gray-600">Time began work</label>
                            <input
                              type="time"
                              value={party.timeBeganWork || ''}
                              onChange={(e) => updateInjuredParty(index, 'timeBeganWork', e.target.value)}
                              className="w-full border rounded px-2 py-1"
                              data-testid={`injured-timebegan-${index}`}
                            />
                          </div>
                          <div>
                            <label className="text-gray-600">Object / substance that harmed worker</label>
                            <input
                              type="text"
                              value={party.objectHarming || ''}
                              onChange={(e) => updateInjuredParty(index, 'objectHarming', e.target.value)}
                              className="w-full border rounded px-2 py-1"
                              placeholder="e.g. falling beam, chemical, machinery"
                              data-testid={`injured-object-${index}`}
                            />
                          </div>
                          <div className="md:col-span-3">
                            <label className="text-gray-600">Activity just before incident</label>
                            <input
                              type="text"
                              value={party.activityBeforeIncident || ''}
                              onChange={(e) => updateInjuredParty(index, 'activityBeforeIncident', e.target.value)}
                              className="w-full border rounded px-2 py-1"
                              placeholder="e.g. climbing ladder to install rebar"
                              data-testid={`injured-activity-${index}`}
                            />
                          </div>
                          <div>
                            <label className="text-gray-600">Physician / provider name</label>
                            <input
                              type="text"
                              value={party.physician || ''}
                              onChange={(e) => updateInjuredParty(index, 'physician', e.target.value)}
                              className="w-full border rounded px-2 py-1"
                              data-testid={`injured-physician-${index}`}
                            />
                          </div>
                          <div>
                            <label className="text-gray-600">Treatment facility</label>
                            <input
                              type="text"
                              value={party.treatmentFacility || ''}
                              onChange={(e) => updateInjuredParty(index, 'treatmentFacility', e.target.value)}
                              className="w-full border rounded px-2 py-1"
                              data-testid={`injured-facility-${index}`}
                            />
                          </div>
                          <div>
                            <label className="text-gray-600">Facility address</label>
                            <input
                              type="text"
                              value={party.treatmentFacilityAddress || ''}
                              onChange={(e) => updateInjuredParty(index, 'treatmentFacilityAddress', e.target.value)}
                              className="w-full border rounded px-2 py-1"
                              placeholder="street, city, state, zip"
                              data-testid={`injured-facilityaddress-${index}`}
                            />
                          </div>
                          <div className="flex items-center gap-4 md:col-span-3 pt-1">
                            <label className="flex items-center gap-1 text-gray-700">
                              <input
                                type="checkbox"
                                checked={!!party.emergencyRoom}
                                onChange={(e) => updateInjuredParty(index, 'emergencyRoom', e.target.checked)}
                                data-testid={`injured-er-${index}`}
                              />
                              Treated in ER
                            </label>
                            <label className="flex items-center gap-1 text-gray-700">
                              <input
                                type="checkbox"
                                checked={!!party.hospitalized}
                                onChange={(e) => updateInjuredParty(index, 'hospitalized', e.target.checked)}
                                data-testid={`injured-hosp-${index}`}
                              />
                              Hospitalized overnight
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-red-700 mt-2">
                {t('incidents.bodyMapHint')}
              </p>
            </div>

            <div>
              <label className="text-sm font-medium">{t('incidents.equipmentInvolved')}</label>
              <input
                type="text"
                value={incidentForm.equipmentInvolved}
                onChange={(e) => setIncidentForm({...incidentForm, equipmentInvolved: e.target.value})}
                placeholder={t('incidents.equipmentPlaceholder')}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium">{t('incidents.propertyDamaged')}</label>
              <input
                type="text"
                value={incidentForm.propertyDamaged}
                onChange={(e) => setIncidentForm({...incidentForm, propertyDamaged: e.target.value})}
                placeholder={t('incidents.propertyDamagePlaceholder')}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>

            {/* AI Assistance for Form Fields */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h4 className="font-semibold text-purple-900 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    {t('incidents.aiFormAssistant')}
                  </h4>
                  <p className="text-sm text-purple-700 mt-1">
                    {t('incidents.aiFormAssistantDesc')}
                  </p>
                </div>
                <Button
                  onClick={handleAIAssist}
                  disabled={aiAssisting || !incidentForm.description || !incidentForm.location}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                  size="sm"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  {aiAssisting ? t('incidents.gettingSuggestions') : t('incidents.getAiSuggestions')}
                </Button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">{t('incidents.correctiveActions')}</label>
              <textarea
                value={incidentForm.correctiveActions}
                onChange={(e) => setIncidentForm({...incidentForm, correctiveActions: e.target.value})}
                placeholder={t('incidents.correctiveActionsPlaceholder')}
                rows={3}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium">{t('incidents.preventiveMeasures')}</label>
              <textarea
                value={incidentForm.preventiveMeasures}
                onChange={(e) => setIncidentForm({...incidentForm, preventiveMeasures: e.target.value})}
                placeholder={t('incidents.preventiveMeasuresPlaceholder')}
                rows={3}
                className="w-full border rounded px-3 py-2 mt-1"
              />
            </div>

            {/* Photo Upload Section */}
            <IncidentPhotoUpload
              reportId={editingReport?.id}
              photos={incidentForm.photos || []}
              onPhotosChange={(photos) => setIncidentForm({...incidentForm, photos, photoUrls: photos.map(p => p.url)})}
              disabled={false}
              pendingPhotos={pendingPhotos}
              onPendingPhotosChange={setPendingPhotos}
            />

            {/* AI Safety Analysis - Show BEFORE submission for new reports and drafts */}
            {(() => {
              const shouldShow = (!editingReport || editingReport.status === 'draft') && !!incidentForm.description && !!incidentForm.preventiveMeasures;
              console.log('AI Section Should Show:', shouldShow, {
                editingReport: !!editingReport,
                hasDescription: !!incidentForm.description,
                hasPreventiveMeasures: !!incidentForm.preventiveMeasures
              });
              return null;
            })()}
            {(!editingReport || editingReport.status === 'draft') && !!incidentForm.description && !!incidentForm.preventiveMeasures && (
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                    <Sparkles className="w-4 h-4" />
                    {t('incidents.aiSafetyAnalysis')}
                  </h4>
                  <Button
                    onClick={async () => {
                      try {
                        setAiAnalyzing(true);
                        toast.info('Running AI Safety Analysis...');
                        
                        // Create temporary draft report to analyze
                        const tempReport = await axiosInstance.post('/incident-reports', {
                          ...incidentForm,
                          status: 'draft'
                        });
                        const reportId = tempReport.data.id;
                        
                        // Upload any pending photos to the draft
                        if (pendingPhotos.length > 0) {
                          toast.info(`Uploading ${pendingPhotos.length} photo(s) for AI analysis...`);
                          const uploadedPhotos = [];
                          for (const pending of pendingPhotos) {
                            try {
                              const formData = new FormData();
                              formData.append('file', pending.file);
                              const photoRes = await axiosInstance.post(
                                `/incident-reports/${reportId}/analyze-photo`,
                                formData,
                                { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
                              );
                              if (photoRes.data.success) {
                                uploadedPhotos.push(photoRes.data.photo);
                              }
                            } catch (photoErr) {
                              console.error('Photo upload failed:', photoErr);
                            }
                          }
                          // Update form with uploaded photos
                          setIncidentForm(prev => ({
                            ...prev,
                            photos: [...(prev.photos || []), ...uploadedPhotos],
                            photoUrls: [...(prev.photoUrls || []), ...uploadedPhotos.map(p => p.url)]
                          }));
                          setPendingPhotos([]);
                        }
                        
                        // Run AI analysis (will now include uploaded photos)
                        const analysisResponse = await axiosInstance.post(`/incident-reports/${reportId}/ai-analyze`);
                        
                        console.log('AI Analysis Response:', analysisResponse.data);
                        
                        // IMPORTANT: Set analysis FIRST
                        setAiAnalysis(analysisResponse.data);
                        
                        // Create editable copy of the analysis
                        if (analysisResponse.data.analysis) {
                          setEditableAiAnalysis({
                            rootCauses: withRowIds(analysisResponse.data.analysis.rootCauses || []),
                            severityValidation: { ...(analysisResponse.data.analysis.severityValidation || {}) },
                            recommendations: withRowIds(analysisResponse.data.analysis.recommendations || []),
                            trainingRecommendations: withRowIds(analysisResponse.data.analysis.trainingRecommendations || []),
                            lessonsLearned: withRowIds(analysisResponse.data.analysis.lessonsLearned || []),
                            preventionStrategies: withRowIds(analysisResponse.data.analysis.preventionStrategies || [])
                          });
                        }
                        
                        // Then set editingReport but keep status as draft
                        setEditingReport({ 
                          ...tempReport.data, 
                          id: reportId,
                          status: 'draft',
                          aiAnalysis: analysisResponse.data,
                          linkedJSAs: tempReport.data.linkedJSAs,
                          linkedAudits: tempReport.data.linkedAudits
                        });
                        
                        toast.success('AI Analysis complete! Review and edit the results below.');
                      } catch (error) {
                        console.error('AI analysis failed:', error);
                        toast.error('Failed to run AI analysis. Please try again.');
                      } finally {
                        setAiAnalyzing(false);
                      }
                    }}
                    disabled={aiAnalyzing || !incidentForm.description || !incidentForm.location}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    size="sm"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    {aiAnalyzing ? t('audits.analyzing') : t('incidents.runAiAnalysis')}
                  </Button>
                </div>
                
                {aiAnalysis && editableAiAnalysis && (
                  <div className="mt-3 bg-white border border-blue-200 rounded p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <h5 className="font-semibold text-base text-blue-900">{t('incidents.aiAnalysisResults')}</h5>
                      <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">{t('incidents.editableLabel')}</span>
                    </div>
                    
                    {/* Root Causes - Editable */}
                    {editableAiAnalysis.rootCauses && editableAiAnalysis.rootCauses.length > 0 && (
                      <div className="border-l-4 border-purple-500 pl-3">
                        <h6 className="font-semibold text-sm text-gray-900 mb-2">{t('incidents.rootCauses')}</h6>
                        <div className="space-y-3">
                          {editableAiAnalysis.rootCauses.map((causeObj, idx) => (
                            <div key={causeObj._rowId || `cause-${idx}`} className="bg-purple-50 rounded p-2 space-y-2">
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={causeObj.category || ''}
                                  onChange={(e) => {
                                    const updated = [...editableAiAnalysis.rootCauses];
                                    updated[idx] = { ...updated[idx], category: e.target.value };
                                    setEditableAiAnalysis({ ...editableAiAnalysis, rootCauses: updated });
                                  }}
                                  className="text-sm font-semibold text-purple-700 bg-white border border-purple-200 rounded px-2 py-1 w-32"
                                  placeholder={t('incidents.category')}
                                />
                                <input
                                  type="text"
                                  value={causeObj.cause || ''}
                                  onChange={(e) => {
                                    const updated = [...editableAiAnalysis.rootCauses];
                                    updated[idx] = { ...updated[idx], cause: e.target.value };
                                    setEditableAiAnalysis({ ...editableAiAnalysis, rootCauses: updated });
                                  }}
                                  className="text-sm text-gray-700 bg-white border border-gray-200 rounded px-2 py-1 flex-1"
                                  placeholder={t('incidents.rootCauseDesc')}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updated = editableAiAnalysis.rootCauses.filter((_, i) => i !== idx);
                                    setEditableAiAnalysis({ ...editableAiAnalysis, rootCauses: updated });
                                  }}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
                                >
                                  ×
                                </Button>
                              </div>
                              <textarea
                                value={causeObj.explanation || ''}
                                onChange={(e) => {
                                  const updated = [...editableAiAnalysis.rootCauses];
                                  updated[idx] = { ...updated[idx], explanation: e.target.value };
                                  setEditableAiAnalysis({ ...editableAiAnalysis, rootCauses: updated });
                                }}
                                className="text-xs text-gray-600 bg-white border border-gray-200 rounded px-2 py-1 w-full"
                                placeholder={t('incidents.explanationOptional')}
                                rows={2}
                              />
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditableAiAnalysis({
                                ...editableAiAnalysis,
                                rootCauses: [...editableAiAnalysis.rootCauses, { _rowId: rowId(), category: '', cause: '', explanation: '' }]
                              });
                            }}
                            className="text-purple-600 border-purple-300"
                          >
                            + {t('incidents.addRootCause')}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Severity Validation - Editable */}
                    {editableAiAnalysis.severityValidation && (
                      <div className="border-l-4 border-orange-500 pl-3">
                        <h6 className="font-semibold text-sm text-gray-900 mb-2">{t('incidents.severityValidation')}</h6>
                        <textarea
                          value={editableAiAnalysis.severityValidation.assessment || ''}
                          onChange={(e) => {
                            setEditableAiAnalysis({
                              ...editableAiAnalysis,
                              severityValidation: { ...editableAiAnalysis.severityValidation, assessment: e.target.value }
                            });
                          }}
                          className="text-sm text-gray-700 bg-white border border-gray-200 rounded px-2 py-1 w-full mb-2"
                          placeholder={t('incidents.severityAssessment')}
                          rows={2}
                        />
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">{t('incidents.suggestedSeverity')}:</span>
                          <select
                            value={editableAiAnalysis.severityValidation.suggestedSeverity || 'Medium'}
                            onChange={(e) => {
                              setEditableAiAnalysis({
                                ...editableAiAnalysis,
                                severityValidation: { ...editableAiAnalysis.severityValidation, suggestedSeverity: e.target.value }
                              });
                            }}
                            className={`text-sm font-semibold rounded px-2 py-1 border ${
                              editableAiAnalysis.severityValidation.suggestedSeverity === 'High' ? 'text-red-600 border-red-300 bg-red-50' :
                              editableAiAnalysis.severityValidation.suggestedSeverity === 'Medium' ? 'text-orange-600 border-orange-300 bg-orange-50' :
                              'text-green-600 border-green-300 bg-green-50'
                            }`}
                          >
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                          </select>
                        </div>
                      </div>
                    )}
                    
                    {/* Recommendations - Editable */}
                    {editableAiAnalysis.recommendations && editableAiAnalysis.recommendations.length > 0 && (
                      <div className="border-l-4 border-green-500 pl-3">
                        <h6 className="font-semibold text-sm text-gray-900 mb-2">{t('incidents.safetyRecommendations')}</h6>
                        <div className="space-y-2">
                          {editableAiAnalysis.recommendations.map((rec, idx) => (
                            <div key={rec._rowId || `rec-${idx}`} className="flex gap-2 items-start bg-green-50 rounded p-2">
                              <select
                                value={rec.priority || 'Medium'}
                                onChange={(e) => {
                                  const updated = [...editableAiAnalysis.recommendations];
                                  updated[idx] = { ...updated[idx], priority: e.target.value };
                                  setEditableAiAnalysis({ ...editableAiAnalysis, recommendations: updated });
                                }}
                                className={`text-xs font-semibold rounded px-1 py-1 border w-20 ${
                                  rec.priority === 'High' ? 'text-red-600 border-red-300 bg-white' :
                                  rec.priority === 'Medium' ? 'text-orange-600 border-orange-300 bg-white' :
                                  'text-green-600 border-green-300 bg-white'
                                }`}
                              >
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                              </select>
                              <input
                                type="text"
                                value={rec.action || ''}
                                onChange={(e) => {
                                  const updated = [...editableAiAnalysis.recommendations];
                                  updated[idx] = { ...updated[idx], action: e.target.value };
                                  setEditableAiAnalysis({ ...editableAiAnalysis, recommendations: updated });
                                }}
                                className="text-sm text-gray-700 bg-white border border-gray-200 rounded px-2 py-1 flex-1"
                                placeholder={t('incidents.recommendationAction')}
                              />
                              <input
                                type="text"
                                value={rec.timeline || ''}
                                onChange={(e) => {
                                  const updated = [...editableAiAnalysis.recommendations];
                                  updated[idx] = { ...updated[idx], timeline: e.target.value };
                                  setEditableAiAnalysis({ ...editableAiAnalysis, recommendations: updated });
                                }}
                                className="text-xs text-gray-600 bg-white border border-gray-200 rounded px-2 py-1 w-24"
                                placeholder={t('common.timeline')}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updated = editableAiAnalysis.recommendations.filter((_, i) => i !== idx);
                                  setEditableAiAnalysis({ ...editableAiAnalysis, recommendations: updated });
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
                              >
                                ×
                              </Button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditableAiAnalysis({
                                ...editableAiAnalysis,
                                recommendations: [...editableAiAnalysis.recommendations, { _rowId: rowId(), priority: 'Medium', action: '', timeline: '' }]
                              });
                            }}
                            className="text-green-600 border-green-300"
                          >
                            + {t('incidents.addRecommendation')}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Training Recommendations - Editable */}
                    {editableAiAnalysis.trainingRecommendations && editableAiAnalysis.trainingRecommendations.length > 0 && (
                      <div className="border-l-4 border-yellow-500 pl-3">
                        <h6 className="font-semibold text-sm text-gray-900 mb-2">{t('incidents.recommendedTraining')}</h6>
                        <div className="space-y-2">
                          {editableAiAnalysis.trainingRecommendations.map((training, idx) => (
                            <div key={rowKey(training, idx, 'train')} className="bg-yellow-50 rounded p-2 space-y-1">
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={training.trainingType || ''}
                                  onChange={(e) => {
                                    const updated = [...editableAiAnalysis.trainingRecommendations];
                                    updated[idx] = { ...updated[idx], trainingType: e.target.value };
                                    setEditableAiAnalysis({ ...editableAiAnalysis, trainingRecommendations: updated });
                                  }}
                                  className="text-sm font-semibold text-yellow-700 bg-white border border-yellow-200 rounded px-2 py-1 flex-1"
                                  placeholder={t('incidents.trainingTypeLabel')}
                                />
                                <input
                                  type="text"
                                  value={training.targetAudience || ''}
                                  onChange={(e) => {
                                    const updated = [...editableAiAnalysis.trainingRecommendations];
                                    updated[idx] = { ...updated[idx], targetAudience: e.target.value };
                                    setEditableAiAnalysis({ ...editableAiAnalysis, trainingRecommendations: updated });
                                  }}
                                  className="text-xs text-gray-600 bg-white border border-gray-200 rounded px-2 py-1 w-32"
                                  placeholder={t('incidents.targetAudience')}
                                />
                                <input
                                  type="text"
                                  value={training.suggestedDuration || ''}
                                  onChange={(e) => {
                                    const updated = [...editableAiAnalysis.trainingRecommendations];
                                    updated[idx] = { ...updated[idx], suggestedDuration: e.target.value };
                                    setEditableAiAnalysis({ ...editableAiAnalysis, trainingRecommendations: updated });
                                  }}
                                  className="text-xs text-gray-600 bg-white border border-gray-200 rounded px-2 py-1 w-24"
                                  placeholder={t('incidents.durationExample')}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const updated = editableAiAnalysis.trainingRecommendations.filter((_, i) => i !== idx);
                                    setEditableAiAnalysis({ ...editableAiAnalysis, trainingRecommendations: updated });
                                  }}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
                                >
                                  ×
                                </Button>
                              </div>
                              <textarea
                                value={training.reasoning || ''}
                                onChange={(e) => {
                                  const updated = [...editableAiAnalysis.trainingRecommendations];
                                  updated[idx] = { ...updated[idx], reasoning: e.target.value };
                                  setEditableAiAnalysis({ ...editableAiAnalysis, trainingRecommendations: updated });
                                }}
                                className="text-xs text-gray-600 bg-white border border-gray-200 rounded px-2 py-1 w-full"
                                placeholder={t('incidents.reasoningLabel')}
                                rows={2}
                              />
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditableAiAnalysis({
                                ...editableAiAnalysis,
                                trainingRecommendations: [...editableAiAnalysis.trainingRecommendations, { _rowId: rowId(), trainingType: '', targetAudience: '', suggestedDuration: '', reasoning: '' }]
                              });
                            }}
                            className="text-yellow-600 border-yellow-300"
                          >
                            + {t('incidents.addTrainingRec')}
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {/* Lessons Learned - Editable */}
                    <div className="border-l-4 border-indigo-500 pl-3">
                      <h6 className="font-semibold text-sm text-gray-900 mb-2">{t('incidents.lessonsLearned')}</h6>
                      <div className="space-y-3">
                        {(editableAiAnalysis.lessonsLearned || []).map((lesson, idx) => (
                          <div key={rowKey(lesson, idx, 'lesson')} className="bg-indigo-50 rounded p-3 space-y-2">
                            <div className="flex gap-2">
                              <select
                                value={lesson.category || 'Prevention'}
                                onChange={(e) => {
                                  const updated = [...(editableAiAnalysis.lessonsLearned || [])];
                                  updated[idx] = { ...updated[idx], category: e.target.value };
                                  setEditableAiAnalysis({ ...editableAiAnalysis, lessonsLearned: updated });
                                }}
                                className="text-xs font-semibold text-indigo-700 bg-white border border-indigo-200 rounded px-2 py-1 w-32"
                              >
                                <option value="Prevention">{t('incidents.lessonPrevention')}</option>
                                <option value="Response">{t('incidents.lessonResponse')}</option>
                                <option value="Communication">{t('incidents.lessonCommunication')}</option>
                                <option value="Equipment">{t('incidents.lessonEquipment')}</option>
                                <option value="Training">{t('incidents.lessonTraining')}</option>
                                <option value="Procedures">{t('incidents.lessonProcedures')}</option>
                                <option value="PPE">{t('incidents.lessonPPE')}</option>
                              </select>
                              <input
                                type="text"
                                value={lesson.applicability || ''}
                                onChange={(e) => {
                                  const updated = [...(editableAiAnalysis.lessonsLearned || [])];
                                  updated[idx] = { ...updated[idx], applicability: e.target.value };
                                  setEditableAiAnalysis({ ...editableAiAnalysis, lessonsLearned: updated });
                                }}
                                className="text-xs text-gray-600 bg-white border border-gray-200 rounded px-2 py-1 w-40"
                                placeholder={t('incidents.appliesToPlaceholder')}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const updated = (editableAiAnalysis.lessonsLearned || []).filter((_, i) => i !== idx);
                                  setEditableAiAnalysis({ ...editableAiAnalysis, lessonsLearned: updated });
                                }}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
                              >
                                ×
                              </Button>
                            </div>
                            <textarea
                              value={lesson.lesson || ''}
                              onChange={(e) => {
                                const updated = [...(editableAiAnalysis.lessonsLearned || [])];
                                updated[idx] = { ...updated[idx], lesson: e.target.value };
                                setEditableAiAnalysis({ ...editableAiAnalysis, lessonsLearned: updated });
                              }}
                              className="text-sm text-gray-700 bg-white border border-gray-200 rounded px-2 py-1 w-full"
                              placeholder={t('incidents.whatDidWeLearn')}
                              rows={2}
                            />
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditableAiAnalysis({
                              ...editableAiAnalysis,
                              lessonsLearned: [...(editableAiAnalysis.lessonsLearned || []), { lesson: '', category: 'Prevention', applicability: 'All workers' }]
                            });
                          }}
                          className="text-indigo-600 border-indigo-300"
                        >
                          + {t('incidents.addLessonLearned')}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-gray-600 mt-2 italic">
                  {t('incidents.editAiHint')}
                </p>
              </div>
            )}

            {/* AI Analysis Section - For editing existing reports */}
            {editingReport && editingReport.id && editingReport.createdAt && editingReport.status === 'submitted' && (
              <div className="mt-4">
                <IncidentAIAnalysis
                  reportId={editingReport.id}
                  existingAnalysis={editingReport.aiAnalysis}
                  onAnalysisUpdate={(analysis) => setAiAnalysis(analysis)}
                />
              </div>
            )}

            {/* Policy Violations Panel */}
            {editingReport && editingReport.id && editingReport.status === 'submitted' && (
              <div className="mt-4">
                <PolicyViolations
                  recordKind="incident"
                  recordId={editingReport.id}
                  initialViolations={editingReport.policyViolations || []}
                  summary={editingReport.policyViolationsSummary || ''}
                  scannedAt={editingReport.policyViolationsScannedAt || ''}
                />
              </div>
            )}

            {/* Related Safety Documents - Only show for saved reports with connections */}
            {editingReport && (editingReport.linkedJSAs?.length > 0 || editingReport.linkedAudits?.length > 0) && (
              <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  {t('incidents.linkedSafetyDocs')}
                  <span className="text-xs font-normal text-gray-600">
                    ({t('incidents.autoLinkedNote')})
                  </span>
                </h4>
                
                {editingReport.linkedJSAs?.length > 0 && (
                  <div className="mb-4">
                    <h5 className="text-sm font-medium text-blue-900 mb-2">{t('incidents.jsaSection')}</h5>
                    <div className="space-y-2">
                      {editingReport.linkedJSAs.map((jsa, idx) => (
                        <div 
                          key={idx} 
                          className="bg-white border border-blue-200 rounded p-3 text-sm hover:bg-blue-50 cursor-pointer transition-colors"
                          onClick={() => handleViewJSA(jsa.id)}
                        >
                          <div className="font-medium text-blue-900">{jsa.task}</div>
                          <div className="text-gray-600 text-xs mt-1">{t('common.location')}: {jsa.location}</div>
                          <div className="text-blue-600 text-xs mt-1">{t('incidents.clickViewDetails')} →</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {editingReport.linkedAudits?.length > 0 && (
                  <div>
                    <h5 className="text-sm font-medium text-green-900 mb-2">{t('incidents.safetyAuditsSection')}</h5>
                    <div className="space-y-2">
                      {editingReport.linkedAudits.map((audit, idx) => (
                        <div 
                          key={idx} 
                          className="bg-white border border-green-200 rounded p-3 text-sm hover:bg-green-50 cursor-pointer transition-colors"
                          onClick={() => handleViewAudit(audit.id)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-green-900">{audit.location}</div>
                            <Badge className={
                              audit.status === 'Pass' ? 'bg-green-600' :
                              audit.status === 'Fail' ? 'bg-red-600' : 'bg-yellow-600'
                            }>
                              {audit.status}
                            </Badge>
                          </div>
                          <div className="text-gray-600 text-xs mt-1">
                            {t('incidents.auditDateLabel')}: {audit.date?.split('T')[0] || t('common.unknown')}
                          </div>
                          <div className="text-green-600 text-xs mt-1">{t('incidents.clickViewDetails')} →</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-3 italic">
                  {t('incidents.linkedDocsAutoNote')}
                </p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowIncidentDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant="outline"
              onClick={() => handleSubmitIncident(true)}
              disabled={submitting}
            >
              {t('incidents.saveDraft')}
            </Button>
            <Button 
              onClick={() => handleSubmitIncident(false)}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? t('common.sending') : t('incidents.submitReport')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit View Dialog */}
      <ViewAuditDialog
        user={user}
        audit={viewingAudit}
        open={showAuditDialog}
        onClose={() => {
          setShowAuditDialog(false);
          setViewingAudit(null);
        }}
        onDelete={() => {
          // Refresh incidents list if needed
          loadIncidentReports();
        }}
      />

      {/* JSA View Dialog */}
      <Dialog open={showJSADialog} onOpenChange={setShowJSADialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900">
              {t('incidents.jsaDetailsTitle')}
            </DialogTitle>
          </DialogHeader>
          {viewingJSA && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg text-gray-900">{viewingJSA.task}</h3>
                <p className="text-sm text-gray-600">Location: {viewingJSA.location}</p>
                <p className="text-sm text-gray-600">Supervisor: {viewingJSA.supervisorName}</p>
              </div>
              
              {viewingJSA.hazards && viewingJSA.hazards.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">{t('incidents.hazardsAndControls')}</h4>
                  <div className="space-y-2">
                    {viewingJSA.hazards.map((hazard, idx) => (
                      <div key={idx} className="bg-orange-50 border border-orange-200 rounded p-3">
                        <div className="font-medium text-orange-900">{hazard.hazard}</div>
                        <div className="text-sm text-gray-700 mt-1">
                          <strong>Control:</strong> {hazard.mitigation || hazard.safetyControl || 'N/A'}
                        </div>
                        {hazard.ppe && (
                          <div className="text-sm text-gray-700 mt-1">
                            <strong>PPE:</strong> {hazard.ppe}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {viewingJSA.recommendedPPE && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">{t('incidents.recommendedPPELabel')}</h4>
                  <p className="text-sm text-gray-700">{viewingJSA.recommendedPPE}</p>
                </div>
              )}

              {viewingJSA.assignedWorkerNames && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">{t('jsa.assignedWorkers')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {typeof viewingJSA.assignedWorkerNames === 'string' ? (
                      <Badge variant="outline">{viewingJSA.assignedWorkerNames}</Badge>
                    ) : Array.isArray(viewingJSA.assignedWorkerNames) ? (
                      viewingJSA.assignedWorkerNames.map((worker, idx) => (
                        <Badge key={idx} variant="outline">{worker}</Badge>
                      ))
                    ) : (
                      <Badge variant="outline">{t('incidents.noWorkersAssigned')}</Badge>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowJSADialog(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupervisorIncidents;
